import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import DealMenu from './DealMenu';
import Loading from '../../common/Loading';
import DealInterestCard from './DealInterestCard';
import DealInterestListItem from './DealInterestListItem';
import { openProfileDrawerToDealProposal } from './profileDrawerHelper';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const GET_COMPANY_SETTINGS = gql`
  query GetCompanySettings {
    company {
      id
      agent_word
      agent_word_fr
      avatar_background_color
      avatar_directory
      primary_color
    }
  }
`;

// Error boundary to catch and handle runtime errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in DealReviewProposals component:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.error_boundary.title" />
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.error_boundary.description" />
                </p>
              </div>
              <div className="mt-4">
                <button 
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.error_boundary.refresh_button" />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const DealReviewProposals = ({ deal, refetchDeal, activeFilter = 'all', currentUser }) => {
  const intl = useIntl();
  const { company: themeCompanySettings } = useSuccessionTheme();
  const [view, setView] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const { data: settingsData } = useQuery(GET_COMPANY_SETTINGS, {
    fetchPolicy: 'cache-first'
  });

  // Get company settings for agent_word
  const companyData = settingsData?.company || currentUser?.company || {};
  const companySettings = {
    ...themeCompanySettings,
    avatar_directory: companyData?.avatar_directory || themeCompanySettings?.avatar_directory,
    avatar_background_color: companyData?.avatar_background_color || themeCompanySettings?.avatar_background_color,
    primary_color: companyData?.primary_color || themeCompanySettings?.primary_color,
  };
  const agentWord = intl.locale === 'fr' ? (companyData.agent_word_fr || 'conseiller') : (companyData.agent_word || 'advisor');
  const agentWordLabel = agentWord.charAt(0).toUpperCase() + agentWord.slice(1);
  const agentWordPlural = agentWord.endsWith('s') ? agentWord : `${agentWord}s`;
  const agentWordPluralLower = agentWordPlural.toLowerCase();
  const agentWordPluralLabel = agentWordPlural.charAt(0).toUpperCase() + agentWordPlural.slice(1);

  // Show loading state if deal is undefined
  if (!deal) {
    return <Loading message={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.loading_message' })} />;
  }
  
  // Filter deal interests based on selected filter and search
  const filteredProposals = () => {
    if (!deal.review_proposals) return [];
    
    // First filter to only get proposals that are in review or declined
    let proposals = deal.review_proposals.filter(
      interest => interest.workflow_state === "reviewing_proposal" || 
                  interest.workflow_state === "declined"
    );
    
    // Apply additional filtering if needed
    if (activeFilter === 'declined') {
      // Filter for declined proposals
      proposals = proposals.filter(interest => interest.workflow_state === "declined");
    }
    
    // Apply search filtering
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      proposals = proposals.filter(
        interest => interest?.inventory_user?.user && (
          (interest.inventory_user.user.name || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.city || '').toLowerCase().includes(term) ||
          (interest.inventory_user.user.province || '').toLowerCase().includes(term)
        )
      );
    }
    
    return proposals;
  };

  // Determine advisor type for each deal interest
  const getAdvisorType = (dealInterest) => {
    if (!dealInterest) return null;
    
    // First check workflow_state which is most reliable
    if (dealInterest.workflow_state === 'following') {
      return 'rolodex'; // Following/rolodex
    } else if (dealInterest.workflow_state === 'follower') {
      return 'follower'; // Follower
    }
    
    // Then check for reason_for_suggestion which indicates a match
    if (dealInterest.reason_for_suggestion) {
      return 'matches'; // Suggested match
    }
    
    return null;
  };

  // Helper function to get badge info based on advisor type
  const getAdvisorBadgeInfo = (dealInterest) => {
    if (!dealInterest) return null;
    
    const type = getAdvisorType(dealInterest);
    
    if (type === 'matches') {
      return { 
        text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.badges.suggested_match' }), 
        color: 'green' 
      };
    } else if (type === 'rolodex') {
      return { 
        text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.badges.rolodex' }), 
        color: 'blue' 
      };
    } else if (type === 'follower') {
      return { 
        text: intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.badges.follower' }), 
        color: 'purple' 
      };
    }
    
    return null;
  };

  const proposals = filteredProposals();
  const dealId = deal.id || '';
  const dealName = deal.deal_name || '';
  console.log(deal, proposals);
  return (
    <div>
      {/* Search and View Options */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 md:mb-0">
          <FormattedMessage 
            id="succession_deals.wizard.steps.deal_review_proposals.title" 
            values={{ count: proposals.length }}
          />
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative" style={{ flex: '0 1 auto', minWidth: '180px', maxWidth: '250px' }}>
            <input
              type="text"
              placeholder={intl.formatMessage(
                { id: 'succession_deals.wizard.steps.deal_review_proposals.search_placeholder_with_agent_word', defaultMessage: 'Search {agentWordPlural}...' },
                { agentWordPlural: agentWordPluralLower }
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                borderRadius: '9999px',
                backgroundColor: 'var(--succession-bg-secondary, #f8fafc)',
                border: '1px solid var(--succession-border, #64748b)',
                color: 'var(--succession-text-primary, #0f172a)',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.3s ease',
                boxShadow: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--succession-primary, #5489B6)';
                e.target.style.boxShadow = '0 0 0 3px rgba(84, 137, 182, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--succession-border, #64748b)';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div className="absolute left-3 top-3 text-succession-text-muted" style={{ pointerEvents: 'none' }}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div className="flex border border-succession-border rounded-lg overflow-hidden flex-shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`px-4 py-2 flex items-center ${view === 'grid' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.view_options.grid" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 flex items-center ${view === 'list' ? 'bg-succession-primary-light text-succession-primary' : 'text-succession-text-secondary'}`}
            >
              <svg className="h-5 w-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.view_options.list" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Stage description */}
      <div className="bg-succession-primary-light border-l-4 border-succession-primary rounded-md p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-succession-primary" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <div className="mt-2 text-sm text-succession-primary">
              <p>
                <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.stage_description.title" />
                &nbsp;
                {proposals.length === 0 ? (
                  activeFilter === 'declined' ? (
                    <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.stage_description.no_declined_proposals" />
                  ) : (
                    <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.stage_description.no_proposals" />
                  )
                ) : (
                  <FormattedMessage 
                    id="succession_deals.wizard.steps.deal_review_proposals.stage_description.proposals_count" 
                    values={{ 
                      count: proposals.length,
                      plural: proposals.length !== 1 ? 's' : ''
                    }}
                  />
                )}
                {activeFilter === 'declined' && proposals.length > 0 && (
                  <span>
                    {' '}
                    <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.stage_description.declined_filter_note" />
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {proposals.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-succession-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-succession-text-primary">
            <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.empty_state.title" />
          </h3>
          <p className="mt-1 text-sm text-succession-text-secondary">
            {activeFilter === 'declined' ? (
              <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.empty_state.no_declined_proposals" />
            ) : (
              <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.empty_state.no_proposals" />
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {view === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {proposals.map((interest) => {
                const advisorBadge = getAdvisorBadgeInfo(interest);
                return (
                  <DealInterestCard
                    key={interest.id}
                    dealInterest={interest}
                    dealName={dealName}
                    companySettings={companySettings}
                    viewDetailsPath={`/succession/deals/${dealName}/proposal/${interest.id}`}
                    viewDetailsLabel={intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.actions.read_expression' })}
                    badgeText={interest.workflow_state === 'declined' ? 
                      intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.status.declined' }) : 
                      intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.status.proposal_received' })}
                    badgeColor={interest.workflow_state === 'declined' ? 'red' : 'blue'}
                  />
                );
              })}
            </div>
          )}
          
          {/* List View */}
          {view === 'list' && (
            <div className="overflow-x-auto border border-succession-border rounded-lg">
              <table className="min-w-full divide-y divide-succession-border">
                <thead className="bg-succession-bg-secondary">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider w-80">
                      {agentWordLabel}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.table_headers.location" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.table_headers.date_submitted" />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-succession-text-secondary uppercase tracking-wider">
                      <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.table_headers.status" />
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">
                        <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.table_headers.actions" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
                  {proposals.map(interest => {
                    const advisor = interest?.inventory_user?.user;
                    if (!advisor) return null;
                    const avatarStyle = getAvatarStyle(advisor.profile_picture, companySettings);
                    
                    return (
                      <tr key={interest.id} className="hover:bg-succession-hover-bg">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="h-10 w-10 rounded-full flex items-center justify-center text-succession-text-secondary text-sm font-semibold overflow-hidden"
                              style={{ backgroundColor: avatarStyle.backgroundColor }}
                            >
                              {advisor.profile_picture ? (
                                <img 
                                  src={avatarStyle.url} 
                                  alt={advisor.name}
                                  className="h-full w-full object-cover"
                                  style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                                />
                              ) : (
                                advisor?.name ? advisor.name.charAt(0).toUpperCase() : '?'
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-succession-text-primary">
                                {advisor?.name || intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.fallback.unknown_advisor' })}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-secondary">
                          {advisor?.city ? `${advisor.city}${advisor.province ? `, ${advisor.province}` : ''}` : 
                            intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.fallback.no_location' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-secondary">
                          {interest.proposal_sent_date 
                            ? format(new Date(interest.proposal_sent_date), 'MMM d, yyyy')
                            : intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.fallback.no_date' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            interest.workflow_state === 'declined' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {interest.workflow_state === 'declined' ? 
                              intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.status.declined' }) : 
                              intl.formatMessage({ id: 'succession_deals.wizard.steps.deal_review_proposals.status.proposal_received' })}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button 
                            onClick={() => {
                              if (interest.inventory_user?.user?.user_name) {
                                openProfileDrawerToDealProposal(interest.inventory_user.user.user_name);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900 flex items-center justify-end bg-transparent border-0 cursor-pointer"
                          >
                            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                            <FormattedMessage id="succession_deals.wizard.steps.deal_review_proposals.actions.read_expression" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DealReviewProposals.propTypes = {
  deal: PropTypes.object,
  refetchDeal: PropTypes.func.isRequired,
  activeFilter: PropTypes.string
};

// Wrap component with error boundary
export default function DealReviewProposalsWithErrorBoundary(props) {
  return (
    <ErrorBoundary>
      <DealReviewProposals {...props} />
    </ErrorBoundary>
  );
} 